// ─────────────────────────────────────────────
// HubWire API Client
// Currently uses mock data. Replace each function
// body with a fetch() call to Cloudflare Workers
// when the backend is ready.
// ─────────────────────────────────────────────

import type {
  Organization,
  SiteSettings,
  Event,
  Registration,
  RegistrationFormData,
  Sponsor,
  Announcement,
  AdminUser,
  ApiResponse,
  LoginCredentials,
} from '../types';

import {
  _mockOrganization,
  _mockSiteSettings,
  _mockEvents,
  _mockRegistrations,
  _mockSponsors,
  _mockAnnouncements,
  _mockAdminUsers,
  _mockPassword,
} from './mockDatabase';

// ─────────────────────────────────────────────
// Utility: simulate async API latency
// ─────────────────────────────────────────────
const delay = (ms = 120) => new Promise((resolve) => setTimeout(resolve, ms));

// ─────────────────────────────────────────────
// Organization
// ─────────────────────────────────────────────
export async function getOrganization(): Promise<ApiResponse<Organization>> {
  await delay();
  return { data: _mockOrganization, error: null, success: true };
}

// ─────────────────────────────────────────────
// Site Settings
// ─────────────────────────────────────────────
export async function getSiteSettings(): Promise<ApiResponse<SiteSettings>> {
  await delay();
  return { data: _mockSiteSettings, error: null, success: true };
}

export async function updateSiteSettings(
  updates: Partial<SiteSettings>
): Promise<ApiResponse<SiteSettings>> {
  await delay();
  // TODO: Replace with PATCH /api/settings when Workers are ready
  Object.assign(_mockSiteSettings, updates);
  return { data: _mockSiteSettings, error: null, success: true };
}

// ─────────────────────────────────────────────
// Events
// ─────────────────────────────────────────────
export async function getEvents(): Promise<ApiResponse<Event[]>> {
  await delay();
  const published = _mockEvents.filter((e) => e.status === 'published');
  return { data: published, error: null, success: true };
}

export async function getAllEvents(): Promise<ApiResponse<Event[]>> {
  await delay();
  return { data: _mockEvents, error: null, success: true };
}

export async function getEventBySlug(slug: string): Promise<ApiResponse<Event>> {
  await delay();
  const event = _mockEvents.find((e) => e.slug === slug) ?? null;
  if (!event) return { data: null, error: 'Event not found', success: false };
  return { data: event, error: null, success: true };
}

export async function getEventById(id: string): Promise<ApiResponse<Event>> {
  await delay();
  const event = _mockEvents.find((e) => e.id === id) ?? null;
  if (!event) return { data: null, error: 'Event not found', success: false };
  return { data: event, error: null, success: true };
}

// ─────────────────────────────────────────────
// Registrations
// ─────────────────────────────────────────────
export async function getRegistrations(): Promise<ApiResponse<Registration[]>> {
  await delay();
  return { data: _mockRegistrations, error: null, success: true };
}

export async function getRegistrationsByEvent(
  eventId: string
): Promise<ApiResponse<Registration[]>> {
  await delay();
  const regs = _mockRegistrations.filter((r) => r.event_id === eventId);
  return { data: regs, error: null, success: true };
}

export async function createRegistration(
  formData: RegistrationFormData,
  eventId: string
): Promise<ApiResponse<Registration>> {
  await delay(300);
  const newReg: Registration = {
    id: `reg_${Date.now()}`,
    organization_id: _mockOrganization.id,
    event_id: eventId,
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    team_name: formData.team_name,
    notes: formData.notes,
    payment_status: 'pending',
    registration_status: 'confirmed',
    created_at: new Date().toISOString(),
  };
  _mockRegistrations.push(newReg);
  // TODO: Trigger email stubs after creation
  await sendRegistrationConfirmation(newReg);
  await sendAdminRegistrationNotification(newReg);
  return { data: newReg, error: null, success: true };
}

// ─────────────────────────────────────────────
// Sponsors
// ─────────────────────────────────────────────
export async function getSponsors(): Promise<ApiResponse<Sponsor[]>> {
  await delay();
  const active = _mockSponsors
    .filter((s) => s.active)
    .sort((a, b) => a.display_order - b.display_order);
  return { data: active, error: null, success: true };
}

export async function getAllSponsors(): Promise<ApiResponse<Sponsor[]>> {
  await delay();
  return { data: _mockSponsors, error: null, success: true };
}

// ─────────────────────────────────────────────
// Announcements
// ─────────────────────────────────────────────
export async function getAnnouncements(): Promise<ApiResponse<Announcement[]>> {
  await delay();
  const published = _mockAnnouncements
    .filter((a) => a.published)
    .sort((a, b) => new Date(b.publish_date).getTime() - new Date(a.publish_date).getTime());
  return { data: published, error: null, success: true };
}

export async function getAllAnnouncements(): Promise<ApiResponse<Announcement[]>> {
  await delay();
  return { data: _mockAnnouncements, error: null, success: true };
}

// ─────────────────────────────────────────────
// Auth
// ─────────────────────────────────────────────
export async function loginAdmin(
  credentials: LoginCredentials
): Promise<ApiResponse<AdminUser>> {
  await delay(400);
  const user = _mockAdminUsers.find(
    (u) => u.email === credentials.email && u.active
  );
  if (!user || credentials.password !== _mockPassword) {
    return { data: null, error: 'Invalid email or password', success: false };
  }
  // TODO: Replace with JWT/session from Cloudflare Workers
  sessionStorage.setItem('hubwire_mock_user_id', user.id);
  return { data: user, error: null, success: true };
}

export async function logoutAdmin(): Promise<ApiResponse<null>> {
  await delay(100);
  sessionStorage.removeItem('hubwire_mock_user_id');
  return { data: null, error: null, success: true };
}

export async function getCurrentAdminUser(): Promise<ApiResponse<AdminUser>> {
  await delay(100);
  const userId = sessionStorage.getItem('hubwire_mock_user_id');
  if (!userId) return { data: null, error: 'Not authenticated', success: false };
  const user = _mockAdminUsers.find((u) => u.id === userId) ?? null;
  if (!user) return { data: null, error: 'User not found', success: false };
  return { data: user, error: null, success: true };
}

// ─────────────────────────────────────────────
// Email Stubs
// (imported here so pages can call through apiClient)
// ─────────────────────────────────────────────
import {
  sendRegistrationConfirmation,
  sendAdminRegistrationNotification,
} from '../modules/email/emailService';

export { sendRegistrationConfirmation, sendAdminRegistrationNotification };

// ─────────────────────────────────────────────
// Events — write operations (Stage 2)
// ─────────────────────────────────────────────
export async function createEvent(
  data: Omit<Event, 'id' | 'organization_id'>
): Promise<ApiResponse<Event>> {
  await delay(300);
  const newEvent: Event = {
    ...data,
    id: `evt_${Date.now()}`,
    organization_id: _mockOrganization.id,
  };
  _mockEvents.push(newEvent);
  return { data: newEvent, error: null, success: true };
}

export async function updateEvent(
  id: string,
  updates: Partial<Omit<Event, 'id' | 'organization_id'>>
): Promise<ApiResponse<Event>> {
  await delay(200);
  const idx = _mockEvents.findIndex((e) => e.id === id);
  if (idx === -1) return { data: null, error: 'Event not found', success: false };
  Object.assign(_mockEvents[idx], updates);
  return { data: _mockEvents[idx], error: null, success: true };
}

// ─────────────────────────────────────────────
// Announcements — write operations (Stage 2)
// ─────────────────────────────────────────────
export async function createAnnouncement(
  data: Omit<Announcement, 'id' | 'organization_id' | 'created_at'>
): Promise<ApiResponse<Announcement>> {
  await delay(200);
  const newAnn: Announcement = {
    ...data,
    id: `ann_${Date.now()}`,
    organization_id: _mockOrganization.id,
    created_at: new Date().toISOString(),
  };
  _mockAnnouncements.push(newAnn);
  return { data: newAnn, error: null, success: true };
}

export async function updateAnnouncement(
  id: string,
  updates: Partial<Omit<Announcement, 'id' | 'organization_id'>>
): Promise<ApiResponse<Announcement>> {
  await delay(200);
  const idx = _mockAnnouncements.findIndex((a) => a.id === id);
  if (idx === -1) return { data: null, error: 'Announcement not found', success: false };
  Object.assign(_mockAnnouncements[idx], updates);
  return { data: _mockAnnouncements[idx], error: null, success: true };
}

// ─────────────────────────────────────────────
// Sponsors — write operations (Stage 2)
// ─────────────────────────────────────────────
export async function createSponsor(
  data: Omit<Sponsor, 'id' | 'organization_id'>
): Promise<ApiResponse<Sponsor>> {
  await delay(200);
  const newSponsor: Sponsor = {
    ...data,
    id: `spo_${Date.now()}`,
    organization_id: _mockOrganization.id,
  };
  _mockSponsors.push(newSponsor);
  return { data: newSponsor, error: null, success: true };
}

export async function updateSponsor(
  id: string,
  updates: Partial<Omit<Sponsor, 'id' | 'organization_id'>>
): Promise<ApiResponse<Sponsor>> {
  await delay(200);
  const idx = _mockSponsors.findIndex((s) => s.id === id);
  if (idx === -1) return { data: null, error: 'Sponsor not found', success: false };
  Object.assign(_mockSponsors[idx], updates);
  return { data: _mockSponsors[idx], error: null, success: true };
}
